import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import api from "../lib/api";
import { ArrowLeft } from "@phosphor-icons/react";
import LeadDetailDrawer from "../components/leads/LeadDetailDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Batch C — full-page lead detail view used by `Open in New Tab`.
 * Reuses the existing LeadDetailDrawer rendering. Honours Sprint-1 BDE
 * scoping by sending requester_* params with the fetch.
 */
export default function LeadDetailPage({ user, logout }) {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [lead, setLead] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError("");
    api.get(`${API}/leads/${leadId}`, {
      params: {
        requester_role: user?.role || "",
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
      },
    }).then((r) => {
      if (!alive) return;
      setLead(r.data);
    }).catch((e) => {
      if (!alive) return;
      const status = e?.response?.status;
      if (status === 403) setError("You do not have access to this lead.");
      else if (status === 404) setError("Lead not found.");
      else setError(e?.response?.data?.detail || "Failed to load lead.");
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [leadId, user?.role, user?.user_id, user?.name]);

  const tabHint = params.get("tab") || "";

  const close = () => navigate("/leads");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="lead-detail-page">
      <div className="border-b border-slate-200 bg-white px-6 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={close} className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-[#002FA7]" data-testid="lead-detail-back">
          <ArrowLeft size={16} weight="bold" /> Back to Leads
        </button>
        <div className="text-xs text-slate-400">/ Lead / {leadId.slice(0, 8)}</div>
      </div>
      {loading && <div className="p-10 text-center text-slate-500" data-testid="lead-detail-loading">Loading…</div>}
      {error && (
        <div className="p-10 text-center" data-testid="lead-detail-error">
          <div className="text-rose-700 font-bold mb-2">{error}</div>
          <button onClick={close} className="text-sm text-[#002FA7] underline">← Back to Leads</button>
        </div>
      )}
      {lead && !error && (
        <LeadDetailDrawer
          lead={{ ...lead, _initialTab: tabHint }}
          user={user}
          onClose={close}
          onLeadChange={() => { /* refresh on mutation */ api.get(`${API}/leads/${leadId}`, { params: { requester_role: user?.role || "", requester_user_id: user?.user_id || "", requester_name: user?.name || "" } }).then((r) => setLead(r.data)).catch(() => {}); }}
        />
      )}
    </div>
  );
}
